import React, { useState, useEffect } from 'react';
import './TradeEntry.css';
import { Plus, Trash2, DollarSign, Smile, Lock, Crown, ArrowRight, Calendar, ChevronLeft, ChevronRight, List, X } from 'lucide-react';
import { getRemainingTrades, hasActiveSubscription } from './subscriptionUtils';
import { getTrades, saveTrade, deleteTrade, getRiskSettings, saveRuleViolation } from './services/firestoreService';
import { analytics } from './firebase';
import { logEvent } from 'firebase/analytics';
import { checkRiskViolations } from './riskCheckUtils';
import RiskViolationPopup from './RiskViolationPopup';

const FREE_LIMIT = 10;

const TradeEntry = ({ currentUser, onNavigate }) => {
  const [trades, setTrades] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [remaining, setRemaining] = useState(FREE_LIMIT);
  const [isPaid, setIsPaid] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    stock: '',
    type: 'BUY',
    entry: '',
    exit: '',
    quantity: '',
    emotion: 'Calm',
    setup: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [riskSettings, setRiskSettings] = useState(null);
  const [pendingTrade, setPendingTrade] = useState(null);
  const [riskViolations, setRiskViolations] = useState([]);
  const [showRiskPopup, setShowRiskPopup] = useState(false);

  // Calendar view state
  const [calendarView, setCalendarView] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalDate, setSelectedCalDate] = useState(null);

  // Emotion options
  const emotions = [
    'Calm',
    'Confident',
    'Fearful',
    'Greedy',
    'Revenge',
    'FOMO',
    'Anxious'
  ];

  // Load user's trades and subscription status from Firestore
  useEffect(() => {
    // Always clear stale data first, regardless of whether currentUser is set
    setTrades([]);
    setIsPaid(false);
    setRemaining(FREE_LIMIT);

    if (currentUser) {
      console.log('[TradeEntry] Loading trades for user:', currentUser.id);
      const load = async () => {
        const [userTrades, paid, settings] = await Promise.all([
          getTrades(currentUser.id),
          hasActiveSubscription(currentUser.id),
          getRiskSettings(currentUser.id)
        ]);
        console.log('[TradeEntry] Fetched', userTrades.length, 'trades for user:', currentUser.id);
        setTrades(userTrades);
        setIsPaid(paid);
        setRemaining(await getRemainingTrades(currentUser.id));
        setRiskSettings(settings);
      };
      load();
    }
  }, [currentUser]);

  // Calculate P&L for a trade
  const calculatePnL = (entry, exit, quantity, type) => {
    const entryPrice = parseFloat(entry);
    const exitPrice = parseFloat(exit);
    const qty = parseFloat(quantity);

    if (type === 'BUY') {
      return (exitPrice - entryPrice) * qty;
    } else {
      return (entryPrice - exitPrice) * qty;
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.stock.trim()) {
      newErrors.stock = 'Stock name required है';
    }

    if (!formData.entry || parseFloat(formData.entry) <= 0) {
      newErrors.entry = 'Valid entry price required है';
    }

    if (!formData.exit || parseFloat(formData.exit) <= 0) {
      newErrors.exit = 'Valid exit price required है';
    }

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = 'Valid quantity required है';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Persist a trade to Firestore and update local state
  const doSaveTrade = async (tradeData) => {
    const docId = await saveTrade(currentUser.id, tradeData);
    logEvent(analytics, 'trade_added', { stock: tradeData.stock, type: tradeData.type, pnl: tradeData.pnl });
    const newTrade = { id: docId, ...tradeData };
    const updatedTrades = [...trades, newTrade];
    setTrades(updatedTrades);
    setRemaining(await getRemainingTrades(currentUser.id));
    setFormData({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      stock: '',
      type: 'BUY',
      entry: '',
      exit: '',
      quantity: '',
      emotion: 'Calm',
      setup: '',
      notes: ''
    });
    setShowForm(false);
    window.dispatchEvent(new CustomEvent('tradefix-trade-logged', {
      detail: { trade: newTrade, allTrades: updatedTrades }
    }));
  };

  // Add new trade — Phase 1: validate, check risk rules; Phase 2: save
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const pnl = calculatePnL(formData.entry, formData.exit, formData.quantity, formData.type);
    const tradeData = {
      userId: currentUser.id,
      date: formData.date,
      time: formData.time || '',
      stock: formData.stock.toUpperCase(),
      type: formData.type,
      entry: parseFloat(formData.entry),
      exit: parseFloat(formData.exit),
      quantity: parseFloat(formData.quantity),
      emotion: formData.emotion,
      setup: formData.setup || '',
      pnl: parseFloat(pnl.toFixed(2)),
      notes: formData.notes,
      createdAt: new Date().toISOString()
    };

    // Check risk rules before saving
    if (riskSettings) {
      const violations = checkRiskViolations(trades, riskSettings, tradeData);
      if (violations.length > 0) {
        setPendingTrade(tradeData);
        setRiskViolations(violations);
        setShowRiskPopup(true);
        return; // Wait for user decision in popup
      }
    }

    // No violations — save immediately
    await doSaveTrade(tradeData);
  };

  // User chose to stop trading — discard the pending trade
  const handleRiskStop = () => {
    setShowRiskPopup(false);
    setPendingTrade(null);
    setRiskViolations([]);
  };

  // User chose to override — log violation and save anyway
  const handleRiskOverride = async () => {
    setShowRiskPopup(false);
    // Log each violation to Firestore
    try {
      for (const v of riskViolations) {
        await saveRuleViolation(currentUser.id, { ...v, action: 'overridden' });
      }
    } catch (err) {
      console.warn('[TradeEntry] Could not log violation:', err.message);
    }
    if (pendingTrade) {
      await doSaveTrade(pendingTrade);
    }
    setPendingTrade(null);
    setRiskViolations([]);
  };

  // Delete trade
  const handleDelete = async (tradeId) => {
    if (window.confirm('क्या आप इस trade को delete करना चाहते हैं?')) {
      await deleteTrade(currentUser.id, tradeId);
      const updatedTrades = trades.filter(t => t.id !== tradeId);
      setTrades(updatedTrades);
      setRemaining(await getRemainingTrades(currentUser.id));
      // Notify App.js so reports & dashboard data refresh immediately
      window.dispatchEvent(new CustomEvent('tradefix-trade-deleted', {
        detail: { tradeId, remainingTrades: updatedTrades }
      }));
      alert('Trade deleted successfully!');
    }
  };

  // Calculate statistics
  const totalPnL = trades.reduce((sum, trade) => sum + trade.pnl, 0);
  const winningTrades = trades.filter(t => t.pnl > 0).length;
  const losingTrades = trades.filter(t => t.pnl < 0).length;
  const winRate = trades.length > 0 ? ((winningTrades / trades.length) * 100).toFixed(1) : 0;
  const avgWin = winningTrades > 0 
    ? trades.filter(t => t.pnl > 0).reduce((sum, t) => sum + t.pnl, 0) / winningTrades 
    : 0;
  const avgLoss = losingTrades > 0
    ? trades.filter(t => t.pnl < 0).reduce((sum, t) => sum + t.pnl, 0) / losingTrades
    : 0;
  const bestTrade  = trades.length > 0 ? Math.max(...trades.map(t => t.pnl)) : 0;
  const worstTrade = trades.length > 0 ? Math.min(...trades.map(t => t.pnl)) : 0;

  const thisMonthPrefix = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const monthlyTradeCount = trades.filter(t => t.date?.startsWith(thisMonthPrefix)).length;
  const limitReached = !isPaid && monthlyTradeCount >= FREE_LIMIT;

  // Build daily P&L map for calendar
  const dailyMap = {};
  trades.forEach(t => {
    if (!dailyMap[t.date]) dailyMap[t.date] = { pnl: 0, trades: [] };
    dailyMap[t.date].pnl += t.pnl;
    dailyMap[t.date].trades.push(t);
  });

  // Calendar computation helpers
  const todayStr = new Date().toISOString().split('T')[0];
  const calYear = calendarDate.getFullYear();
  const calMonth = calendarDate.getMonth();
  const calFirstDay = new Date(calYear, calMonth, 1).getDay();
  const calDaysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const calMonthPrefix = `${calYear}-${String(calMonth + 1).padStart(2, '0')}`;
  const calMonthEntries = Object.entries(dailyMap).filter(([d]) => d.startsWith(calMonthPrefix));
  const calGreenDays = calMonthEntries.filter(([, v]) => v.pnl > 0).length;
  const calRedDays = calMonthEntries.filter(([, v]) => v.pnl < 0).length;
  const calNetPnL = calMonthEntries.reduce((sum, [, v]) => sum + v.pnl, 0);
  const calCells = [];
  for (let i = 0; i < calFirstDay; i++) calCells.push(null);
  for (let d = 1; d <= calDaysInMonth; d++) calCells.push(d);
  const calMonthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // Performance Summary stats for the calendar's current month
  const calMonthTrades = calMonthEntries.flatMap(([, v]) => v.trades);
  const calMonthWins = calMonthTrades.filter(t => t.pnl > 0);
  const calMonthLosses = calMonthTrades.filter(t => t.pnl < 0);
  const calAvgWin = calMonthWins.length > 0 ? calMonthWins.reduce((s, t) => s + t.pnl, 0) / calMonthWins.length : 0;
  const calAvgLoss = calMonthLosses.length > 0 ? calMonthLosses.reduce((s, t) => s + t.pnl, 0) / calMonthLosses.length : 0;
  const calBestTrade = calMonthTrades.length > 0 ? Math.max(...calMonthTrades.map(t => t.pnl)) : 0;

  return (
    <div className="space-y-6">
      {/* Risk Rule Violation popup — shown before saving a trade that breaks rules */}
      {showRiskPopup && (
        <RiskViolationPopup
          violations={riskViolations}
          onStop={handleRiskStop}
          onOverride={handleRiskOverride}
          isPreSave={true}
        />
      )}

      {/* Upgrade banner — shown to free users who have ≤1 trade left */}
      {!isPaid && remaining <= 1 && (
        <div className={`te-alert ${limitReached ? 'te-alert-danger' : 'te-alert-warning'}`}>
          <div className="flex items-center space-x-3">
            <Lock size={20} style={{ color: limitReached ? 'var(--danger)' : 'var(--warning)' }} />
            <div>
              <p className="font-bold text-sm" style={{ color: limitReached ? 'var(--danger)' : 'var(--warning)' }}>
                {limitReached
                  ? 'Trade limit reached — upgrade to add more trades'
                  : `Only ${remaining} free trade${remaining === 1 ? '' : 's'} remaining`}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Free plan includes {FREE_LIMIT} trades. Upgrade for unlimited trading.
              </p>
            </div>
          </div>
          <button
            onClick={() => onNavigate && onNavigate('pricing')}
            className="te-btn-primary"
          >
            <Crown size={14} />
            <span>Upgrade</span>
            <ArrowRight size={14} />
          </button>
        </div>
      )}

      {/* Header with Stats */}
      <div className="te-card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="te-page-title">My Trades</h2>
            {!isPaid && (
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-semibold" style={{ color: limitReached ? 'var(--danger)' : 'var(--accent)' }}>
                  {monthlyTradeCount}/{FREE_LIMIT} trades this month
                </span>
                {!limitReached && (
                  <span className="ml-1">— {FREE_LIMIT - monthlyTradeCount} remaining</span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={() => {
              if (limitReached) {
                onNavigate && onNavigate('pricing');
              } else {
                setShowForm(!showForm);
              }
            }}
            disabled={!limitReached && showForm}
            className="te-btn-primary"
          >
            {limitReached ? (
              <>
                <Lock size={16} />
                <span>Upgrade to Add More</span>
              </>
            ) : (
              <>
                <Plus size={18} />
                <span>Add Trade</span>
              </>
            )}
          </button>
        </div>

        {/* View Toggle */}
        <div className="flex justify-end mt-4">
          <div className="te-view-toggle">
            <button
              onClick={() => setCalendarView(false)}
              className={`te-view-btn ${!calendarView ? 'active' : ''}`}
            >
              <List size={13} />
              <span>List View</span>
            </button>
            <button
              onClick={() => setCalendarView(true)}
              className={`te-view-btn ${calendarView ? 'active' : ''}`}
            >
              <Calendar size={13} />
              <span>Calendar View</span>
            </button>
          </div>
        </div>
      </div>

      {/* Add Trade Form */}
      {showForm && !limitReached && (
        <div className="te-card">
          <h3 className="te-page-title" style={{ marginBottom: 16 }}>Add New Trade</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date */}
              <div>
                <label className="te-form-label">
                  Trade Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="te-input"
                />
              </div>

              {/* Time */}
              <div>
                <label className="te-form-label">
                  Trade Time
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  className="te-input"
                />
              </div>

              {/* Stock Name */}
              <div>
                <label className="te-form-label">
                  Stock Name <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="text"
                  name="stock"
                  value={formData.stock}
                  onChange={handleChange}
                  placeholder="e.g., RELIANCE, TCS"
                  className={`te-input${errors.stock ? ' te-input-error' : ''}`}
                />
                {errors.stock && (
                  <p className="te-error-msg">{errors.stock}</p>
                )}
              </div>

              {/* Trade Type */}
              <div>
                <label className="te-form-label">
                  Trade Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="te-input"
                >
                  <option value="BUY">BUY (Long)</option>
                  <option value="SELL">SELL (Short)</option>
                </select>
              </div>

              {/* Emotion */}
              <div>
                <label className="te-form-label flex items-center space-x-2">
                  <Smile size={16} style={{ color: 'var(--accent)' }} />
                  <span>Emotion During Trade</span>
                </label>
                <select
                  name="emotion"
                  value={formData.emotion}
                  onChange={handleChange}
                  className="te-input"
                >
                  {emotions.map(emotion => (
                    <option key={emotion} value={emotion}>{emotion}</option>
                  ))}
                </select>
                <p style={{ fontSize: 12, color: 'var(--accent)', marginTop: 4 }}>
                  🧠 Trade लेते समय आप कैसा feel कर रहे थे?
                </p>
              </div>

              {/* Setup Type */}
              <div>
                <label className="te-form-label">
                  Setup Type
                </label>
                <select
                  name="setup"
                  value={formData.setup}
                  onChange={handleChange}
                  className="te-input"
                >
                  <option value="">Select Setup</option>
                  <option value="Breakout">Breakout</option>
                  <option value="Breakdown">Breakdown</option>
                  <option value="Trend Follow">Trend Follow</option>
                  <option value="Reversal">Reversal</option>
                  <option value="Support/Resistance">Support/Resistance</option>
                  <option value="Moving Average">Moving Average</option>
                  <option value="VWAP">VWAP</option>
                  <option value="Gap Up/Down">Gap Up/Down</option>
                  <option value="Scalp">Scalp</option>
                  <option value="News Based">News Based</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Entry Price */}
              <div>
                <label className="te-form-label">
                  Entry Price <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="number"
                  name="entry"
                  value={formData.entry}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="e.g., 2450.50"
                  className={`te-input${errors.entry ? ' te-input-error' : ''}`}
                />
                {errors.entry && (
                  <p className="te-error-msg">{errors.entry}</p>
                )}
              </div>

              {/* Exit Price */}
              <div>
                <label className="te-form-label">
                  Exit Price <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="number"
                  name="exit"
                  value={formData.exit}
                  onChange={handleChange}
                  step="0.01"
                  placeholder="e.g., 2480.75"
                  className={`te-input${errors.exit ? ' te-input-error' : ''}`}
                />
                {errors.exit && (
                  <p className="te-error-msg">{errors.exit}</p>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label className="te-form-label">
                  Quantity <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  step="1"
                  placeholder="e.g., 10"
                  className={`te-input${errors.quantity ? ' te-input-error' : ''}`}
                />
                {errors.quantity && (
                  <p className="te-error-msg">{errors.quantity}</p>
                )}
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="te-form-label">
                  Notes (Optional)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Any notes about this trade..."
                  className="te-input"
                />
              </div>
            </div>

            {/* Live P&L Preview */}
            {formData.entry && formData.exit && formData.quantity && (
              <div className="te-stat" style={{ marginBottom: 0 }}>
                <p className="te-stat-label">P&L Preview</p>
                <p className="te-stat-value" style={{ color: calculatePnL(formData.entry, formData.exit, formData.quantity, formData.type) >= 0 ? 'var(--profit)' : 'var(--loss)', fontSize: 24 }}>
                  ₹{calculatePnL(formData.entry, formData.exit, formData.quantity, formData.type).toFixed(2)}
                </p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={limitReached}
                className="te-btn-primary"
                style={{ flex: 1, justifyContent: 'center', padding: '12px 20px', opacity: limitReached ? 0.5 : 1, cursor: limitReached ? 'not-allowed' : 'pointer' }}
              >
                Add Trade
                {!isPaid && !limitReached && remaining <= 2 && (
                  <span className="ml-2 text-xs opacity-75">({remaining} left)</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData({
                    date: new Date().toISOString().split('T')[0],
                    time: new Date().toTimeString().slice(0, 5),
                    stock: '',
                    type: 'BUY',
                    entry: '',
                    exit: '',
                    quantity: '',
                    emotion: 'Calm',
                    setup: '',
                    notes: ''
                  });
                  setErrors({});
                }}
                className="te-btn-outline"
                style={{ flex: 1, justifyContent: 'center', padding: '12px 20px' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List View */}
      {!calendarView && (() => {
        const sortedTrades = [...trades].sort((a, b) => {
          if (b.date !== a.date) return new Date(b.date) - new Date(a.date);
          return (b.time || '').localeCompare(a.time || '');
        });
        const tradesByDate = {};
        sortedTrades.forEach(t => {
          if (!tradesByDate[t.date]) tradesByDate[t.date] = [];
          tradesByDate[t.date].push(t);
        });
        const dateKeys = Object.keys(tradesByDate).sort((a, b) => new Date(b) - new Date(a));
        return (
          <>
            {/* Summary bar */}
            {trades.length > 0 && (
              <div className="trades-summary">
                <div className="summary-item">
                  <span className="summary-label">Total Trades</span>
                  <span className="summary-value">{trades.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total P&L</span>
                  <span className={`summary-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
                    {totalPnL >= 0 ? '+' : ''}₹{totalPnL.toFixed(0)}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Win Rate</span>
                  <span className="summary-value">{winRate}%</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Best Trade</span>
                  <span className="summary-value positive">+₹{bestTrade.toFixed(0)}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Worst Trade</span>
                  <span className="summary-value negative">₹{worstTrade.toFixed(0)}</span>
                </div>
              </div>
            )}

            {/* Trade list */}
            <div className="te-card">
              <div className="trades-header">
                <span className="trades-title">Trade History</span>
                <span className="trades-count">{trades.length} trade{trades.length !== 1 ? 's' : ''}</span>
              </div>

              {trades.length === 0 ? (
                <div className="te-empty">
                  <DollarSign size={48} className="mx-auto" />
                  <p className="font-semibold" style={{ color: 'var(--text-muted)' }}>No trades yet</p>
                  <p>Click "Add Trade" to log your first trade</p>
                </div>
              ) : (
                dateKeys.map(date => (
                  <div key={date}>
                    <div className="date-group-label">
                      {new Date(date + 'T12:00:00').toLocaleDateString('en-IN', {
                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                      }).toUpperCase()}
                    </div>
                    {tradesByDate[date].map(trade => (
                      <div key={trade.id} className="trade-card">
                        {/* Col 1: Date */}
                        <div className="trade-date">
                          <span className="trade-day">
                            {new Date(date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                          {trade.time && <span className="trade-time">{trade.time}</span>}
                        </div>
                        {/* Col 2: Symbol + tags */}
                        <div className="trade-main">
                          <span className="trade-symbol">{trade.stock}</span>
                          <span className={trade.type === 'BUY' ? 'tag-buy' : 'tag-sell'}>{trade.type}</span>
                          {trade.emotion && <span className="tag-emotion">{trade.emotion}</span>}
                          {trade.setup && <span className="tag-setup">{trade.setup}</span>}
                        </div>
                        {/* Col 3: Entry → Exit */}
                        <div className="trade-prices">₹{trade.entry} → ₹{trade.exit}</div>
                        {/* Col 4: Qty */}
                        <div className="trade-qty">Qty: {trade.quantity}</div>
                        {/* Col 5: P&L */}
                        <div className={`trade-pnl ${trade.pnl >= 0 ? 'positive' : 'negative'}`}>
                          {trade.pnl >= 0 ? '+' : ''}₹{Math.abs(trade.pnl).toFixed(0)}
                        </div>
                        {/* Col 6: Delete */}
                        <button
                          className="trade-delete"
                          onClick={() => handleDelete(trade.id)}
                          title="Delete trade"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </>
        );
      })()}

      {/* Summary Stats — list view only */}
      {!calendarView && trades.length > 0 && (
        <div className="te-card">
          <h3 className="te-page-title" style={{ fontSize: 16, marginBottom: 16 }}>Performance Summary</h3>
          <div className="te-stat-grid">
            <div className="te-stat text-center">
              <p className="te-stat-label">Avg Win</p>
              <p className="te-stat-value" style={{ color: 'var(--profit)' }}>₹{avgWin.toFixed(0)}</p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-label">Avg Loss</p>
              <p className="te-stat-value" style={{ color: 'var(--loss)' }}>₹{Math.abs(avgLoss).toFixed(0)}</p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-label">Risk:Reward</p>
              <p className="te-stat-value" style={{ color: 'var(--accent)' }}>
                1:{avgLoss !== 0 ? (Math.abs(avgWin / avgLoss)).toFixed(2) : '0'}
              </p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-label">Best Trade</p>
              <p className="te-stat-value" style={{ color: 'var(--accent)' }}>
                ₹{trades.length > 0 ? Math.max(...trades.map(t => t.pnl)).toFixed(0) : '0'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Calendar View ─────────────────────────────────────────────────── */}
      {calendarView && (
        <div className="te-card">
          {/* Monthly summary stats */}
          <div className="te-perf-grid" style={{ marginBottom: 20 }}>
            <div className="te-stat text-center">
              <p className="te-stat-value" style={{ color: 'var(--profit)' }}>{calGreenDays}</p>
              <p className="te-stat-label" style={{ marginTop: 4 }}>Green Days</p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-value" style={{ color: 'var(--loss)' }}>{calRedDays}</p>
              <p className="te-stat-label" style={{ marginTop: 4 }}>Red Days</p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-value" style={{ color: calNetPnL >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                {calNetPnL >= 0 ? '+' : ''}₹{Math.abs(calNetPnL) >= 1000
                  ? `${(calNetPnL / 1000).toFixed(1)}k`
                  : calNetPnL.toFixed(0)}
              </p>
              <p className="te-stat-label" style={{ marginTop: 4 }}>Monthly P&L</p>
            </div>
          </div>

          {/* Month navigation */}
          <div className="te-cal-header">
            <button
              onClick={() => setCalendarDate(new Date(calYear, calMonth - 1, 1))}
              className="te-cal-nav"
            >
              <ChevronLeft size={16} />
            </button>
            <h3 className="te-cal-month">
              {calMonthNames[calMonth]} {calYear}
            </h3>
            <button
              onClick={() => setCalendarDate(new Date(calYear, calMonth + 1, 1))}
              className="te-cal-nav"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day headers */}
          <div className="te-cal-grid" style={{ marginBottom: 4 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} className="te-cal-day-name">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="te-cal-grid">
            {calCells.map((day, i) => {
              if (day === null) return <div key={`e-${i}`} style={{ minHeight: 52 }} />;
              const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayData = dailyMap[dateStr];
              const isToday = dateStr === todayStr;
              const hasTrades = !!dayData;
              const isProfit = hasTrades && dayData.pnl > 0;
              const isLoss = hasTrades && dayData.pnl < 0;
              return (
                <div
                  key={dateStr}
                  onClick={() => hasTrades && setSelectedCalDate(dateStr)}
                  className={[
                    'te-cal-cell',
                    'relative p-1.5',
                    hasTrades ? 'cursor-pointer' : 'cursor-default',
                    isProfit ? 'te-cal-cell-profit' : '',
                    isLoss ? 'te-cal-cell-loss' : '',
                    isToday ? 'te-cal-cell-today' : '',
                  ].join(' ')}
                  style={!hasTrades ? { background: 'var(--bg-primary)', border: '1px solid var(--border)' } : {}}
                >
                  <span className="te-cal-cell-num" style={{ color: isToday ? 'var(--accent)' : isProfit ? 'var(--profit)' : isLoss ? 'var(--loss)' : 'var(--text-muted)' }}>
                    {day}
                  </span>
                  {hasTrades && (
                    <span className="te-cal-cell-pnl" style={{ color: isProfit ? 'var(--profit)' : 'var(--loss)' }}>
                      {isProfit ? '+' : ''}₹{Math.abs(dayData.pnl) >= 1000
                        ? `${(dayData.pnl / 1000).toFixed(1)}k`
                        : dayData.pnl.toFixed(0)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {trades.length === 0 && (
            <p className="te-empty" style={{ padding: '24px 0 0' }}>No trades to display. Add trades to see calendar.</p>
          )}
        </div>
      )}

      {/* Performance Summary — calendar view (monthly) */}
      {calendarView && calMonthTrades.length > 0 && (
        <div className="te-card">
          <h3 className="te-page-title" style={{ fontSize: 16, marginBottom: 16 }}>
            Performance Summary{' '}
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>— {calMonthNames[calMonth]} {calYear}</span>
          </h3>
          <div className="te-stat-grid">
            <div className="te-stat text-center">
              <p className="te-stat-label">Avg Win</p>
              <p className="te-stat-value" style={{ color: 'var(--profit)' }}>₹{calAvgWin.toFixed(0)}</p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-label">Avg Loss</p>
              <p className="te-stat-value" style={{ color: 'var(--loss)' }}>₹{Math.abs(calAvgLoss).toFixed(0)}</p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-label">Risk:Reward</p>
              <p className="te-stat-value" style={{ color: 'var(--accent)' }}>
                1:{calAvgLoss !== 0 ? (Math.abs(calAvgWin / calAvgLoss)).toFixed(2) : '0'}
              </p>
            </div>
            <div className="te-stat text-center">
              <p className="te-stat-label">Best Trade</p>
              <p className="te-stat-value" style={{ color: 'var(--accent)' }}>₹{calBestTrade.toFixed(0)}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Selected Date Modal (bottom sheet) ───────────────────────────── */}
      {selectedCalDate && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0" onClick={() => setSelectedCalDate(null)}>
          <div
            className="rounded-t-2xl w-full max-w-2xl max-h-[75vh] overflow-hidden flex flex-col"
            style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
              <div>
                <h3 className="te-page-title" style={{ fontSize: 15 }}>
                  {new Date(selectedCalDate + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
                <p className="te-date mt-0.5">
                  {dailyMap[selectedCalDate]?.trades.length} trade{dailyMap[selectedCalDate]?.trades.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button onClick={() => setSelectedCalDate(null)} className="te-btn-outline" style={{ padding: '6px 10px' }}>
                <X size={18} />
              </button>
            </div>

            {/* Trade list */}
            <div className="overflow-y-auto flex-1 p-4 space-y-3">
              {(dailyMap[selectedCalDate]?.trades || [])
                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                .map((trade, i) => (
                  <div key={i} className="te-trade-row flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="te-symbol" style={{ fontSize: 14 }}>{trade.stock}</span>
                        <span className={`te-badge ${trade.type === 'BUY' ? 'te-badge-buy' : 'te-badge-sell'}`} style={{ fontSize: 11, padding: '2px 8px' }}>
                          {trade.type}
                        </span>
                        {trade.emotion && (
                          <span className="te-badge te-badge-emotion" style={{ fontSize: 11, padding: '2px 8px' }}>{trade.emotion}</span>
                        )}
                      </div>
                      <p className="te-meta">
                        Entry ₹{trade.entry} → Exit ₹{trade.exit} · Qty {trade.quantity}
                      </p>
                      {trade.time && <p className="te-date mt-0.5">{trade.time}</p>}
                    </div>
                    <span className="te-pnl-value ml-3 flex-shrink-0" style={{ color: trade.pnl >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                      {trade.pnl >= 0 ? '+' : ''}₹{Math.abs(trade.pnl).toFixed(0)}
                    </span>
                  </div>
                ))}
            </div>

            {/* Day total footer */}
            <div className="px-5 py-3 flex items-center justify-between flex-shrink-0" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-card)' }}>
              <span className="te-meta">Net P&L for the day</span>
              <span className="te-pnl-value" style={{ color: (dailyMap[selectedCalDate]?.pnl || 0) >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                {(dailyMap[selectedCalDate]?.pnl || 0) >= 0 ? '+' : ''}₹{Math.abs(dailyMap[selectedCalDate]?.pnl || 0).toFixed(0)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeEntry;
